import { AfterViewInit, Directive, ElementRef, Input, NgZone, OnDestroy } from '@angular/core';

/**
 * `[appStickyShadow]` — adiciona a classe `stuck` a um elemento `position: sticky`
 * SOMENTE quando ele "descola" (está grudado no topo), e a remove quando volta pro
 * "lar" (posição natural). Assim dá pra mostrar sombra só enquanto flutua.
 *
 * Como: um sentinel de altura 0 é inserido logo antes do elemento; um
 * IntersectionObserver com `rootMargin` igual à linha onde o elemento passa a
 * grudar dispara ao cruzá-la. CSS puro não expõe o estado "stuck", por isso o observer.
 *
 * A linha de disparo = `gap - top`, onde `top` é o offset sticky e `gap` é o espaço
 * acima do host (seu `margin-top`). Ambos são lidos do ESTILO COMPUTADO — estáveis,
 * independentes de scroll/estado grudado —, e a medição é (re)feita num rAF após o
 * layout assentar e a cada `resize`. Sem isso, medir uma única vez no `ngAfterViewInit`
 * pega valores ainda não assentados (classes de layout, `--chrome-top-offset`), o que
 * fazia a barra "decolar antes" (sombra cedo demais).
 */
@Directive({
  selector: '[appStickyShadow]',
})
export class StickyShadowDirective implements AfterViewInit, OnDestroy {
  /** Classe aplicada enquanto o elemento está grudado (descolado do lar). */
  @Input() stuckClass = 'stuck';

  private sentinel?: HTMLElement;
  private observer?: IntersectionObserver;
  private rafId?: number;

  constructor(private el: ElementRef<HTMLElement>, private zone: NgZone) {}

  ngAfterViewInit(): void {
    const host = this.el.nativeElement;
    if (typeof IntersectionObserver === 'undefined' || !host.parentElement) { return; }

    // Sentinel (altura 0) no fluxo, logo antes do host → fica na posição-lar dele.
    // Como não tem margem, o `margin-top` do host aplica-se INTEGRALMENTE entre os
    // dois (sem colapso), logo o gap real = margin-top computado do host.
    const sentinel = document.createElement('div');
    sentinel.setAttribute('aria-hidden', 'true');
    sentinel.style.height = '0';
    host.parentElement.insertBefore(sentinel, host);
    this.sentinel = sentinel;

    // rAF/resize fora da zona (não precisa disparar change detection). A medição
    // inicial é diferida p/ o layout (classes de chrome/sidebar, vars CSS) assentar.
    this.zone.runOutsideAngular(() => {
      this.scheduleMeasure();
      window.addEventListener('resize', this.onResize, { passive: true });
    });
  }

  private onResize = (): void => this.scheduleMeasure();

  private scheduleMeasure(): void {
    if (this.rafId != null) { cancelAnimationFrame(this.rafId); }
    this.rafId = requestAnimationFrame(() => {
      this.rafId = undefined;
      this.measure();
    });
  }

  private measure(): void {
    const host = this.el.nativeElement;
    if (!this.sentinel) { return; }

    const cs = getComputedStyle(host);
    const top = parseFloat(cs.top) || 0;          // offset sticky (resolve o calc/var)
    const gap = parseFloat(cs.marginTop) || 0;    // espaço acima do host, no lar

    this.observer?.disconnect();
    this.observer = new IntersectionObserver(
      ([entry]) => host.classList.toggle(this.stuckClass, !entry.isIntersecting),
      { rootMargin: `${gap - top}px 0px 0px 0px`, threshold: 0 },
    );
    this.observer.observe(this.sentinel);
  }

  ngOnDestroy(): void {
    if (this.rafId != null) { cancelAnimationFrame(this.rafId); }
    window.removeEventListener('resize', this.onResize);
    this.observer?.disconnect();
    this.sentinel?.remove();
  }
}
